/**
 * @file 常用方法
 * @author fightingliu
 */
import _ from 'lodash';
import MediaIcon from '../../assets/images/file/media.svg';
import ImageIcon from '../../assets/images/file/image.svg';
import DocIcon from '../../assets/images/file/doc.svg';
import XlsIcon from '../../assets/images/file/xls.svg';
import PPTIcon from '../../assets/images/file/ppt.svg';
import EmailIcon from '../../assets/images/file/email.svg';
import PDFIcon from '../../assets/images/file/pdf.svg';

const VIDEO_LIST = ['mp4', 'mov', 'm4v', '3gp', 'avi', 'm3u8', 'webm']; // 视频格式
const MUSIC_LIST = ['mp3', 'wma', 'wav', 'ape', 'flac', 'ogg', 'aac']; // 音乐格式
const PDF_LIST = ['pdf']; // PDF 格式
const DOC_LIST = ['doc', 'docx', 'psd']; // Word 格式
const XLS_LIST = ['xls', 'xlsx']; // Excel格式
const PPT_LIST = ['ppt', 'pptx']; // PPT格式
const IMAGE_LIST = ['jpg', 'png', 'gif']; // 图片格式
const EMAIL_LIST = ['eml']; // 邮件格式

// 附件后缀对应文件类型
const fileTypeList = [
  {
    name: VIDEO_LIST,
    type: 'video',
    path: MediaIcon,
  },
  {
    name: MUSIC_LIST,
    type: 'music',
    path: MediaIcon,
  },
  {
    name: IMAGE_LIST,
    type: 'images',
    path: ImageIcon,
  },
  {
    name: DOC_LIST,
    type: 'doc',
    path: DocIcon,
  },
  {
    name: XLS_LIST,
    type: 'xls',
    path: XlsIcon,
  },
  {
    name: PPT_LIST,
    type: 'ppt',
    path: PPTIcon,
  },
  {
    name: EMAIL_LIST,
    type: 'email',
    path: EmailIcon,
  },
  {
    name: PDF_LIST,
    type: 'pdf',
    path: PDFIcon,
  },
];
// 根据url获取文件的类型
export const getFileInfoByUrl: any = (fileUrl: string) => {
  const index = _.lastIndexOf(fileUrl, '.');
  const unit = `${fileUrl}`.substring(index + 1, _.size(fileUrl)).toLowerCase();
  const matchList = _.filter(fileTypeList, ({ name = '' }) => _.includes(name, unit));
  if (_.isEmpty(matchList) || _.isEmpty(_.head(matchList))) {
    return { type: '', path: '' };
  }
  return _.head(matchList);
};

export const openWin = (url: string) => {
  // window.open 会导致性能问题，表现为当前页面卡死
  // 使用 a 标签 hack
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

// HTML转义
export const HTMLEncode = (html: string) => {
  let temp = document.createElement('div');
  temp.textContent !== null ? (temp.textContent = html) : (temp.innerText = html);
  const output = temp.innerHTML;
  temp = null;
  return output;
};

// HTML反转义
export const HTMLDecode = (text: string) => {
  let temp = document.createElement('div');
  temp.innerHTML = text;
  const output = temp.innerText || temp.textContent;
  temp = null;
  return output;
};

// 开户行业数据，过滤无效数据
const getFilterCsIndustry = (data: any[], isFilterItem: any) => {
  const canUseData = _.filter(data, ({ status = '' }) => !_.includes(['hidden', 'disabled'], status));
  return _.map(canUseData, (item: { status: any; display_name: any }) => {
    const { status, display_name: displayName } = item;
    return {
      ...item,
      ...(status === 'deprecated' && {
        display_name: `${displayName}(废弃)`,
        disabled: !isFilterItem, // 筛选项，不需要
      }),
    };
  });
};

/**
 * 格式化[开户行业数据]
 * @param {Object} data // 服务端返回的行业数据
 * @param {Boolean} isShortId   // isShortId 用来兼容不同取值场景
 * @param {Boolean} hasLevelInfoInValue value中是否包含层级信息
 * @param {Boolean} pure 返回原始数据，不做特殊处理
 */
const formatCsIndustryTree = (
  {
    data = [],
    isShortId,
    hasLevelInfoInValue,
    isFilterItem, // 为筛选项
  }: any,
  pure: boolean
) => {
  if (pure) return data;
  const formatData = getFilterCsIndustry(data, isFilterItem);
  return _.reduce(
    formatData,
    (prevTreeData: any, curTreeNode: any) => {
      // Hard Code: isShortId
      const {
        industry_short_id: shortID,
        industry_id: industryID,
        display_name: displayName,
        parent_id: parentID,
        level,
        disabled,
      } = curTreeNode;
      const value = isShortId ? shortID : industryID;

      const curNode = {
        disabled,
        value: hasLevelInfoInValue ? `${level}-${value}` : value,
        industry_id: industryID,
        desc: displayName,
        parent_id: parentID,
        level,
      };
      if (level === 1) {
        prevTreeData.push(curNode);
        return prevTreeData;
      }

      // todo: 统一写法
      const params = {
        industry_id: parentID,
      };
      const curParentNode = _.find(prevTreeData, params) || {};
      if (curParentNode.options) {
        curParentNode.options.push(curNode);
      } else {
        curParentNode.options = [curNode];
      }
      return prevTreeData;
    },
    []
  );
};

/**
 * 格式化[运营行业数据]
 * @param {Object} data // 服务端返回的运营行业数据
 * @param {Boolean} hasLevelInfoInValue value中是否包含层级信息
 */
const formatIndustryTree = (
  data: any,
  {
    hasLevelInfoInValue = false,
    useIdValue = true,
    parentId = 0,
    level = 1,
    id = 'id',
    name = 'name',
    children = 'children',
  } = {}
) =>
  _.map(data, (item: any) => ({
    level,
    value: hasLevelInfoInValue ? `${level}-${item[useIdValue ? id : name]}` : item[useIdValue ? id : name],
    desc: item[name],
    parent_id: parentId,
    ...(item[children]
      ? {
          options: formatIndustryTree(item[children], {
            hasLevelInfoInValue,
            useIdValue,
            parentId,
            id,
            name,
            children,
            level: level + 1,
          }),
        }
      : {}),
  }));

/**
 * 根据运营行业ID查找运营行业名称
 * @param {object[]} industryTree 服务端返回的运营行业数据
 * @param {number[]} industryIdList 待查找的ID列表：[level1Id, level2Id, level3Id]
 * @returns {string[]} 运营行业ID映射表
 */
const getIndustryNamesById = (industryTree: any, industryIdList: any) => {
  // 运营行业树解耦太耗性能，遂逐级查找
  const industryNameList = [] as any;
  let currentNode = industryTree;
  _.map(industryIdList, (item: any) => {
    const currentLevelObject = _.find(currentNode, ['id', item]);
    industryNameList.push(_.get(currentLevelObject, 'name', ''));
    currentNode = _.get(currentLevelObject, 'children', []);
  });
  return industryNameList;
};

/**
 * ID映射表[开户行业数据]：根据'industry_short_id'建立
 * @param {object} data 服务端返回的开户行业数据
 * @param pure 返回原始数据
 * @returns {object} 开户行业'industry_short_id'映射表
 */
const getCsIndustryMap = (data: any, pure: boolean) => {
  if (!data) {
    return [];
  }
  if (pure) {
    return data;
  }
  return _.keyBy(data, 'industry_short_id');
};

/**
 * 格式化[组织架构（销售、运营归属团队）]
 * @param {Object} data // 服务端返回的架构数据
 */
const formatTeamTree = (data: any) => {
  if (!data) {
    return [];
  }
  const formatChildren = (id: any, children: any) =>
    _.map(children, (child: any) => ({
      value: child.team_id,
      desc: child.name,
      parent_id: id,
      ...(child.children
        ? {
            options: formatChildren(child.team_id, child.children),
          }
        : {}),
    }));
  return formatChildren(data.team_id, data.children);
};

/**
 * 格式化[所在地选择树]
 * @param {Object} data // 服务端返回的架构数据
 * @param pure 返回原始数据
 */
const formatAreaTree = (data: any, pure: boolean) => {
  if (pure) {
    return data;
  }
  return _.reduce(
    data,
    (prevTreeData: any, { code = 0, name = '', parent_code: parentCode = 0, level = 0 }) => {
      const curNode = {
        value: code,
        desc: name,
        parent_id: parentCode,
        level,
      };
      if (level === 0) {
        prevTreeData.push(curNode);
        return prevTreeData;
      }

      const curParentNode = _.find(prevTreeData, { value: parentCode }) || {};
      if (curParentNode.options) {
        curParentNode.options.push(curNode);
      } else {
        curParentNode.options = [curNode];
      }
      return prevTreeData;
    },
    []
  );
};

/**
 * 格式化[财务标签]
 * @param {Object} data // 服务端返回的财务标签数据
 */
const formatFinancialData = (data: any) => {
  const groupData = _.groupBy(data, 'type');
  return _.map(groupData, (items: any, key: any) => {
    const options = _.map(items, ({ name = '', id = '' }) => ({
      value: id,
      desc: name,
    }));
    return {
      desc: key,
      value: key,
      options,
    };
  });
};

export const currencyFormatV2 = (num: any = 0, isCents: boolean): string => {
  num = num.toString().replace(/\$|,/g, '');
  if (isNaN(num)) num = '0';
  const sign = num == (num = Math.abs(num));
  num = Math.floor(num * 100 + 0.50000000001);
  let cents: string | number = num % 100;
  num = Math.floor(num / 100).toString();
  if (cents < 10) cents = `0${cents}`;
  for (let i = 0; i < Math.floor((num.length - (1 + i)) / 3); i++)
    num = `${num.substring(0, num.length - (4 * i + 3))},${num.substring(num.length - (4 * i + 3))}`;
  if (isCents) {
    return `${(sign ? '' : '-') + num}.${cents}`;
  }
  return (sign ? '' : '-') + num;
};

export default {
  openWin,
  getFileInfoByUrl,
  HTMLDecode,
  HTMLEncode,
  getFilterCsIndustry,
  formatCsIndustryTree,
  formatIndustryTree,
  formatTeamTree,
  formatAreaTree,
  formatFinancialData,
  getIndustryNamesById,
  getCsIndustryMap,
  currencyFormatV2,
};
